/* eslint-disable react/button-has-type */
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { APChartComponent, InfoTooltip, SortTypes } from '@landscape/ui-lib'
import { SectionLoader } from '@cb/apricot-react'
import { AxiosInstance } from 'axios'

import { Source, useApContextData } from './useApContextData'
import { transformApData } from './chartUtils'
import { SchoolSelect } from './SchoolSelect'

import './APContextDashboard.scss'

export interface APContextDashboardProps {
  ai?: string
  diCode?: string
  dataSource: Source
  hsData?: any
  restClient?: AxiosInstance
}

/** ──────────────────────────────────────────────────────────────────── */

const StyledHighSchoolPanel = styled.div`
  & h6 {
    margin-bottom: 5px;
  }
  &.col-xs-12 .chart-high-school {
    padding-right: 5px;
  }
  & .key-value-pairs-block {
    margin-bottom: 5px;
  }
`

const StyledFootnote = styled.div`
  font-size: 0.875rem;
  color: #666;
  margin-top: 8px;
  padding-left: 32px;
  padding-right: 32px;
  font-style: italic;
`

export const APContextDashboard: React.FC<APContextDashboardProps> = ({ ai = "", diCode = "", dataSource, hsData, restClient }) => {
  const [distOption, setDistOption] = useState<'dist' | 'avg'>('dist')
  const [studentsOpt, setStudentsOpt] = useState<'nStudents' | 'pStudents'>('nStudents')
  const [aiCode, setAiCode] = useState<string>(ai)
  const [apExamYear, setApExamYear] = useState<string>('2024')
  const [sortType, setSortType] = useState<SortTypes>(SortTypes.DEFAULT_ORDER)
  const [highSchool, setHighSchool] = useState<string>('')
  const [showSpinner, setShowSpinner] = useState(false)

  /* ─── fetch AP‑Context (dataSource‑agnostic) ─────────────────────── */
  const {
    data: apContextData,
    loading: apContextLoading,
    error: apContextError,
  } = useApContextData({ aiCode, diCode, restClient, source: dataSource })

  /* ─── transform once we have data ────────────────────────────────── */
  const apDataCurrent = transformApData(apContextData, apExamYear)

  const handleSelect = (_, { school, aiCode: newCode }) => {
    setHighSchool(school)
    setAiCode(newCode)
  }

  useEffect(() => {
    setShowSpinner(apContextLoading)
  }, [apContextLoading])

  const chartProps = {
    setDistOption,
    distOption,
    setStudentsOption: setStudentsOpt,
    studentsOption: studentsOpt,
    apExamYear,
    setApExamYear,
    isInteractive: true,
    apDataCurrent,
    sortType,
    setSortType,
    apContextData,
  }

  return (
    <div id="main-content">
      <div className="cb-panel">
        <div className="container">
          {/* School picker */}
          {hsData && (
            <div className="globalInputs">
              <div className="school-select-container display-flex">
                <div className="col-xs-8">
                  <SchoolSelect
                    data={hsData}
                    id="school-select"
                    label="Select a school"
                    onSelect={handleSelect}
                    scrollLimit={10}
                  />
                </div>
              </div>
            </div>
          )}
          {/* Loader / error / chart */}
          {apContextLoading && (
            <div className="loaderBlock">
              <SectionLoader show={showSpinner} ariaLabel="charts-spinner" />
            </div>
          )}

          {apContextError && (
            <div
              className="container display-flex flex-column justify-content-center align-items-center"
              id="apChartMsg"
            >
              <label className="cb-margin-top-16 cb-margin-bottom-16 cb-font-weight-bold cb-band cb-band-panel">
                Error loading AP Context Data for {highSchool}: {apContextError.message}
              </label>
            </div>
          )}

          {apContextData && (
            <>
              <div className="display-flex flex-xs-column justify-md-content-between cb-margin-top-16 cb-margin-bottom-16 cb-padding-right-16 cb-padding-left-16">
                <StyledHighSchoolPanel className="row">
                  <div className="chart-high-school">
                    <h6>AP Detail for: {apContextData.getApContextData.highschool.name}</h6>
                    <div className="flex-box">
                      <div className="key-value-pairs-block">
                        <p>Seniors Taking AP:</p>
                        <div className="value-block">
                          <strong className="marginRight5">
                            {isNaN(
                              parseInt(
                                apContextData.getApContextData.highschool.a_pct_taking_ap,
                                10
                              )
                            )
                              ? 'NA'
                              : apContextData.getApContextData.highschool.a_pct_taking_ap + '%'}
                          </strong>
                          <InfoTooltip textContent="Three-year average of the percentage of the senior class who have taken at least one AP Exam." />
                        </div>
                      </div>
                      <div className="key-value-pairs-block">
                        <p>Average AP Exams Taken:</p>
                        <div className="value-block">
                          <strong className="marginRight5">
                            {isNaN(
                              parseFloat(apContextData.getApContextData.highschool.adv_aptaken)
                            )
                              ? 'NA'
                              : parseFloat(
                                  apContextData.getApContextData.highschool.adv_aptaken
                                ).toFixed(2)}
                          </strong>
                          <InfoTooltip textContent="3-year average of the number of AP Exams taken by seniors from the high school who took at least one AP Exam." />
                        </div>
                      </div>
                      <div className="key-value-pairs-block">
                        <p>Average AP Score:</p>
                        <div className="value-block">
                          <strong className="marginRight5">
                            {isNaN(
                              parseFloat(apContextData.getApContextData.highschool.adv_avgapscore)
                            )
                              ? 'NA'
                              : parseFloat(
                                  apContextData.getApContextData.highschool.adv_avgapscore
                                ).toFixed(2)}
                          </strong>
                          <InfoTooltip textContent="Three-year average of AP Exam scores across college-bound seniors from the high school who took AP Exam(s)." />
                        </div>
                      </div>
                      <div className="key-value-pairs-block">
                        <p>Unique Exams Administered:</p>
                        <div className="value-block">
                          <strong className="marginRight5">
                            {isNaN(
                              parseFloat(apContextData.getApContextData.highschool.adv_aptestavail)
                            )
                              ? 'NA'
                              : parseFloat(
                                  apContextData.getApContextData.highschool.adv_aptestavail
                                ).toFixed(0)}
                          </strong>
                          <InfoTooltip textContent="Three-year average of the number of unique AP Exams taken by college-bound seniors at the high school." />
                        </div>
                      </div>
                      <div className="key-value-pairs-block">
                        <p>AP Capstone School:</p>
                        <div className="value-block">
                          <strong className="marginRight5">
                            {apContextData.getApContextData.highschool.ddv_apcapstone == '1'
                              ? 'Yes'
                              : 'No'}
                          </strong>
                          {apContextData.getApContextData.highschool.ddv_apcapstone && (
                            <InfoTooltip textContent="High school approved to offer AP Capstone Diploma program." />
                          )}
                        </div>
                      </div>
                      <div className="key-value-pairs-block">
                        <p>Senior Class Size:</p>
                        <div className="value-block">
                          <strong className="marginRight5">
                            {isNaN(
                              parseFloat(apContextData.getApContextData.highschool.a_sr_class_size)
                            )
                              ? 'NA'
                              : parseFloat(
                                  apContextData.getApContextData.highschool.a_sr_class_size
                                ).toFixed(0)}
                          </strong>
                          {apContextData.getApContextData.highschool.a_sr_class_size && (
                            <InfoTooltip textContent="This measure is based on a 3-year average of senior class size." />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </StyledHighSchoolPanel>
              </div>
              <APChartComponent {...chartProps} />
              <StyledFootnote>
                * indicates an exam that underwent standard setting in 2023 or 2024; the score
                averages and distributions displayed in the dashboard will differ depending on the
                year the applicant took the exam.
              </StyledFootnote>
            </>
          )}

          {!apContextLoading && !apContextData && !apContextError && (
            <div
              className="container display-flex flex-column justify-content-center align-items-center"
              id="apChartMsg"
            >
              <label className="cb-margin-top-16 cb-margin-bottom-16 cb-font-weight-bold cb-band cb-band-panel">
                Search a school to view the AP Context Dashboard
              </label>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
